/**
 * Service for managing the Payroll Index document in Firestore.
 *
 * The payroll index is a single document (companies/{companyId}/payrollIndex/master)
 * containing a `runs` array that tracks all draft/pending/completed payroll runs.
 * It enables fast conflict detection without querying every payrollRuns document,
 * and (PRD_DtrWriteModel C1/C9) is the source of truth for *derived* DTR locks.
 *
 * Because it is now lock-bearing, every write goes through a Firestore
 * transaction (read-modify-write the `runs` array) so concurrent updates can't
 * clobber each other (C9). The approve/reject flows fold their index write into
 * their own `runTransaction` for atomicity with the run-status flip — they use the
 * exported `payrollIndexRef` + `mergeIndexData` helpers to do the set in-tx.
 */

import { doc, getDoc, runTransaction } from 'firebase/firestore';
import { db } from '../firebase/config';

const DEFAULT_INDEX = { runs: [], lastArchiveNumber: 0, lastHousekeepingAt: null };

/** The single per-company payroll index document ref. */
export const payrollIndexRef = (companyId) =>
  doc(db, 'companies', companyId, 'payrollIndex', 'master');

/**
 * Pure: upsert a run entry into a `runs` array by `runId` — merge `fields` into
 * the matching entry, or append a fresh one if absent. Idempotent, so a retry
 * (or a lagging earlier write) self-corrects rather than duplicating.
 */
export const upsertRunEntry = (runs, runId, fields) => {
  const list = Array.isArray(runs) ? runs : [];
  const idx = list.findIndex((r) => r.runId === runId);
  if (idx === -1) return [...list, { ...fields, runId }];
  const next = list.slice();
  next[idx] = { ...next[idx], ...fields, runId };
  return next;
};

/** Pure: the full master-doc data after upserting one run entry (C9 in-tx use). */
export const mergeIndexData = (data, runId, fields) => {
  const base = data || DEFAULT_INDEX;
  return { ...base, runs: upsertRunEntry(base.runs, runId, fields) };
};

/** Pure: the full master-doc data after removing one run entry. */
export const removeIndexData = (data, runId) => {
  const base = data || DEFAULT_INDEX;
  return { ...base, runs: (base.runs || []).filter((r) => r.runId !== runId) };
};

/**
 * Fetch the payroll index for a company.
 * Returns the runs array (empty array if no index exists yet).
 *
 * NOTE: swallows errors → `[]` for the *advisory* conflict-detection callers.
 * Lock derivation must NOT use this — it needs read errors surfaced so it can
 * fail-closed (PRD_DtrWriteModel C10a); see dtrLockDerivation.loadActiveLocks.
 *
 * @param {string} companyId
 * @returns {Promise<Array>} The runs array from the index
 */
export const fetchPayrollIndex = async (companyId) => {
  try {
    const snap = await getDoc(payrollIndexRef(companyId));
    if (snap.exists()) {
      return snap.data().runs || [];
    }
    return [];
  } catch (error) {
    console.error('[payrollIndexService] Error fetching payroll index:', error);
    return [];
  }
};

/**
 * Transactionally upsert a run entry (read-modify-write `runs` — no clobber, C9).
 * Creates the master document if it doesn't exist.
 */
const upsertIndexEntry = async (companyId, runId, fields) => {
  const ref = payrollIndexRef(companyId);
  await runTransaction(db, async (tx) => {
    const snap = await tx.get(ref);
    tx.set(ref, mergeIndexData(snap.exists() ? snap.data() : null, runId, fields));
  });
};

/**
 * Add a run entry to the payroll index (transactional upsert; creates master if
 * absent). Upsert-by-`runId` so a re-save can't duplicate the entry.
 *
 * @param {string} companyId
 * @param {Object} runEntry - { runId, periodStart, periodEnd, status, employeeIds }
 */
export const addIndexEntry = async (companyId, runEntry) =>
  upsertIndexEntry(companyId, runEntry.runId, runEntry);

/**
 * Update a run entry in the index (status change, employee-list change). Now a
 * transactional upsert: merges `updates` into the matching entry, or creates it
 * if missing (robust against a dropped earlier write — C9/C10).
 *
 * @param {string} companyId
 * @param {string} runId
 * @param {Object} updates - Fields to merge into the matching run entry
 */
export const updateIndexEntry = async (companyId, runId, updates) =>
  upsertIndexEntry(companyId, runId, updates);

/**
 * Remove a run entry from the index (e.g., when a draft is deleted). Transactional.
 *
 * @param {string} companyId
 * @param {string} runId
 */
export const removeIndexEntry = async (companyId, runId) => {
  const ref = payrollIndexRef(companyId);
  await runTransaction(db, async (tx) => {
    const snap = await tx.get(ref);
    if (!snap.exists()) return;
    tx.set(ref, removeIndexData(snap.data(), runId));
  });
};
